import { computed } from "vue";

import { backendOld } from "~/api";
import { BillingPeriod, Subscription } from "~/shared/enums";
import { useTenantStore } from "~/stores";

export const APPSUMO_ACCOUNT_URL = "https://appsumo.com/account/products/";

export const manageBilling = async () => {
  const tenantStore = useTenantStore();
  const returnPath = tenantStore.$router.currentRoute.value.fullPath;
  const { data } = await backendOld.billing.createPortalSession(returnPath);
  window.location.href = data.redirectUrl;
};

export const createOrUpdateSubscription = async (subscription: Subscription, period?: BillingPeriod) => {
  const tenantStore = useTenantStore();
  const returnPath = tenantStore.$router.currentRoute.value.fullPath;
  const periodNorm = period ?? BillingPeriod.ANNUALLY;

  if (subscription === Subscription.PERSONAL) {
    manageBilling();
    return false;
  }

  if (tenantStore.everSubscribed) {
    const { data } = await backendOld.billing.updateSubscriptionTier(subscription, periodNorm, returnPath);
    if (data.redirectUrl) {
      window.location.href = data.redirectUrl;
      return false;
    }
    return true;
  }

  const { data } = await backendOld.billing.createCheckoutSession(subscription, periodNorm, returnPath);
  window.location.href = data.redirectUrl;
  return false;
};

export const useUpgrade = (tenantStore: ReturnType<typeof import("~/stores").useTenantStore>) => {
  const usingAppSumo = computed(() => tenantStore.subscriptionNorm === Subscription.APP_SUMO);
  const canUpgradeAppSumoTier = computed(
    () => usingAppSumo.value && tenantStore.appSumoTierNorm !== null && tenantStore.appSumoTierNorm < 5
  );

  const subscriptionText = computed(() => {
    if (usingAppSumo.value) {
      return `AppSumo Tier ${tenantStore.appSumoTierNorm}`;
    }
    return tenantStore.subscriptionNorm;
  });

  const upgradeCta = computed(() => {
    if (usingAppSumo.value && canUpgradeAppSumoTier.value) {
      return "Upgrade tier";
    }
    return "Upgrade";
  });

  const getUpgradeDialogTitle = (subscription: Subscription): string => {
    if (usingAppSumo.value && canUpgradeAppSumoTier.value) {
      return "Upgrade your AppSumo tier";
    }
    return `Upgrade to ${subscription}`;
  };

  const getUpgradeDescription = (featureAction: string, subscription: Subscription): string => {
    if (subscription === Subscription.APP_SUMO && usingAppSumo.value && canUpgradeAppSumoTier.value) {
      return `Your workspace is on AppSumo Tier ${tenantStore.appSumoTierNorm}. To ${featureAction}, upgrade to a higher AppSumo tier.`;
    }
    const subscriptionNorm = subscription === Subscription.APP_SUMO ? Subscription.PREMIUM : subscription;
    return `Your workspace is on the ${tenantStore.subscriptionNorm} plan. To ${featureAction} and enjoy other benefits, upgrade to ${subscriptionNorm}.`;
  };

  const handleUpgradeClick = (subscription: Subscription, period?: BillingPeriod) => {
    if (usingAppSumo.value && canUpgradeAppSumoTier.value) {
      window.open(APPSUMO_ACCOUNT_URL, "_blank");
    } else {
      createOrUpdateSubscription(subscription, period);
    }
  };

  return {
    canUpgradeAppSumoTier,
    subscriptionText,
    upgradeCta,
    getUpgradeDialogTitle,
    getUpgradeDescription,
    handleUpgradeClick,
  };
};
